// App shell: top bar (date / cash / runway / End Turn), nav rail, screen router,
// event teletype, and the campaign overlays (scenario select, system menu, game
// over). P1: the shell is scenario-aware — garage campaigns boot into a
// three-screen nav (GARAGE / TREE / MONEY) that grows into the full company nav
// on incorporation day, the cheapest big story beat we will ever ship.

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { ContentPack } from "../engine/types.js";
import { loadBrowserContent, SCENARIOS, type ScenarioMeta } from "./browserContent.js";
import { useMediaQuery, useNarrow } from "./media.js";
import { GameSession } from "./session.js";
import { beginSession, track } from "./telemetry.js";
import { money, monthLabel, pct } from "./format.js";
import { AllocationStrip } from "./components/AllocationStrip.js";
import { Dashboard } from "./screens/Dashboard.js";
import { Pipeline } from "./screens/Pipeline.js";
import { Markets } from "./screens/Markets.js";
import { People } from "./screens/People.js";
import { Nodes } from "./screens/Nodes.js";
import { Money } from "./screens/Money.js";
import { Boardroom } from "./screens/Boardroom.js";
import { Street } from "./screens/Street.js";
import { Partners } from "./screens/Partners.js";
import { Garage } from "./screens/Garage.js";
import { Tree } from "./screens/Tree.js";
import { ShareLinks } from "./components/ShareLinks.js";

export type ScreenId =
  | "dashboard"
  | "pipeline"
  | "markets"
  | "people"
  | "nodes"
  | "money"
  | "boardroom"
  | "street"
  | "partners"
  | "garage"
  | "tree";

interface Game {
  content: ContentPack;
  session: GameSession;
}

/** Installed to a home screen (or otherwise chrome-less)? iOS exposes
 *  navigator.standalone; everyone else the display-mode media feature. */
function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone), (display-mode: fullscreen)").matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

/** Boot: the autosave names its scenario; build that pack, then replay. */
function bootGame(): Game | null {
  const scenarioId = GameSession.savedScenarioId();
  if (!scenarioId) return null;
  try {
    const content = loadBrowserContent(scenarioId);
    const session = GameSession.load(content);
    return session ? { content, session } : null;
  } catch {
    return null;
  }
}

export function App() {
  const [game, setGame] = useState<Game | null>(bootGame);
  const gameRef = useRef(game); // heartbeat reads the live session without re-arming
  gameRef.current = game;

  useEffect(() => {
    const g = gameRef.current;
    // device class at session start: how many players are actually on phones?
    const device = {
      vw: window.innerWidth,
      vh: window.innerHeight,
      coarse: window.matchMedia("(pointer: coarse)").matches,
      standalone: isStandalone(),
    };
    beginSession(
      g
        ? { resumed: true, scenarioId: g.content.scenario.id, turn: g.session.getSnapshot().turn, contentVersion: g.content.version, ...device }
        : { resumed: false, ...device },
      () => gameRef.current?.session.getSnapshot().turn ?? 0,
    );
  }, []);

  const newGame = (scenarioId: string, seed: number) => {
    GameSession.clearSave();
    track("new_game", { scenarioId, seed });
    const content = loadBrowserContent(scenarioId);
    setGame({ content, session: new GameSession(content, seed) });
  };

  if (!game) return <ScenarioSelect onStart={newGame} />;
  // no key: swapping sessions in place is fine — useSyncExternalStore re-subscribes
  // on the new session and the orphaned-screen effect re-homes the router
  return <Campaign game={game} onNewGame={newGame} onLoad={setGame} onChooseScenario={() => setGame(null)} />;
}

function Campaign({
  game,
  onNewGame,
  onLoad,
  onChooseScenario,
}: {
  game: Game;
  onNewGame: (scenarioId: string, seed: number) => void;
  onLoad: (g: Game) => void;
  onChooseScenario: () => void;
}) {
  const { content, session } = game;
  const [screen, setScreen] = useState<ScreenId>(content.scenario.garage ? "garage" : "dashboard");
  const [showSystem, setShowSystem] = useState(false);
  const snap = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const { view, pending } = snap;
  const feedRef = useRef<HTMLDivElement>(null);

  // responsive shell (docs/SPEC_MOBILE_UI.md): CSS re-arranges the grid; React
  // only mirrors the same breakpoints where the structure genuinely differs
  const isNarrow = useNarrow();
  const isShortLandscape = useMediaQuery("(max-height: 500px) and (orientation: landscape)");
  const feedDrawer = useMediaQuery("(max-width: 999px)"); // feed is a drawer below 1000px
  const bottomBar = isNarrow && !isShortLandscape; // landscape phones keep the side rail
  const [feedOpen, setFeedOpen] = useState(false);
  const [feedSeen, setFeedSeen] = useState(() => session.getSnapshot().feed.length);
  const unread = Math.max(0, snap.feed.length - feedSeen);
  useEffect(() => {
    if (feedOpen) setFeedSeen(snap.feed.length);
  }, [feedOpen, snap.feed.length]);

  // with the feed in a drawer, rejected orders surface as a toast instead
  const rejections = snap.report?.rejectedCommands ?? [];
  const [toastTurn, setToastTurn] = useState(-1); // turn whose toast was dismissed
  const showToast = feedDrawer && rejections.length > 0 && toastTurn !== snap.turn;
  useEffect(() => {
    if (!showToast) return;
    const t = setTimeout(() => setToastTurn(snap.turn), 6000);
    return () => clearTimeout(t);
  }, [showToast, snap.turn]);

  const hasTree = !!content.scenario.treeId;
  const inGarage = view.stage === "garage";

  const kpi = view.kpiHistory[view.kpiHistory.length - 1];
  const ttm = view.finance.ttmRevenue.reduce((a, b) => a + b, 0);
  const boardDecisions = (view.finance.openTermSheet ? 1 : 0) + (view.ipo.process?.phase === "pricing" ? 1 : 0);
  const askDecisions = view.asks.length;
  const pendingDecisions =
    view.projects.filter((p) => p.phase === "respinDecision").length +
    (view.projects.some((p) => p.phase === "waitingTapeout" && view.nodes[p.nodeId]!.status === "qualified") ? 1 : 0) +
    boardDecisions +
    askDecisions;

  // the nav literally grows when the company does (pivot spec §11)
  const screens: { id: ScreenId; label: string; badge?: number }[] = inGarage
    ? [
        { id: "garage", label: "GARAGE", badge: view.finance.openTermSheet ? 1 : 0 },
        { id: "tree", label: "TREE" },
        { id: "money", label: "MONEY" },
      ]
    : [
        { id: "dashboard", label: "HQ", badge: askDecisions },
        ...(hasTree ? [{ id: "tree" as ScreenId, label: "TREE" }] : []),
        { id: "pipeline", label: "PIPELINE", badge: pendingDecisions - boardDecisions - askDecisions },
        { id: "markets", label: hasTree ? "MARKET" : "MARKETS" },
        { id: "people", label: "PEOPLE" },
        { id: "nodes", label: "NODES" },
        { id: "money", label: "MONEY" },
        { id: "boardroom", label: "BOARDROOM", badge: view.finance.openTermSheet ? 1 : 0 },
        { id: "street", label: "THE STREET", badge: view.ipo.process?.phase === "pricing" ? 1 : 0 },
        { id: "partners", label: "PARTNERS" },
      ];

  // incorporation day (or a loaded save) can orphan the current screen — re-home
  // at RENDER time (the state sync below runs a frame later, after the screen
  // list has already changed under us; rendering a dead GARAGE screen crashes)
  const shownScreen = screens.some((s) => s.id === screen) ? screen : inGarage ? "garage" : "dashboard";
  useEffect(() => {
    if (shownScreen !== screen) setScreen(shownScreen);
  }, [shownScreen, screen]);

  // death only happens inside step(), so this handler is also the game_over choke point
  const endTurn = () => {
    if (!session.endTurn()) return;
    const after = session.getSnapshot();
    track("turn_end", { turn: after.turn, monthIndex: after.view.monthIndex });
    if (after.view.dead) {
      track("game_over", { turn: after.turn, scenarioId: content.scenario.id, reason: after.view.deathReason ?? "bankrupt" });
    }
  };

  // Enter ends the turn (keyboard-first play, design §13) unless typing in a field
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (e.key === "Escape") {
        setShowSystem(false);
        setFeedOpen(false);
      }
      if (showSystem) return; // the system menu owns the keyboard
      if (e.key === "Enter" && !view.dead) endTurn();
      const idx = e.key === "0" ? 10 : Number(e.key);
      if (idx >= 1 && idx <= screens.length) setScreen(screens[idx - 1]!.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    feedRef.current?.scrollTo({ top: 0 });
  }, [snap.turn]);

  const screenEl = {
    dashboard: <Dashboard snap={snap} content={content} session={session} go={setScreen} />,
    pipeline: <Pipeline snap={snap} content={content} session={session} />,
    markets: <Markets snap={snap} content={content} session={session} />,
    people: <People snap={snap} content={content} session={session} />,
    nodes: <Nodes snap={snap} content={content} session={session} />,
    money: <Money snap={snap} content={content} session={session} />,
    boardroom: <Boardroom snap={snap} content={content} session={session} />,
    street: <Street snap={snap} content={content} session={session} />,
    partners: <Partners snap={snap} content={content} session={session} />,
    garage: <Garage snap={snap} content={content} session={session} />,
    tree: <Tree snap={snap} content={content} session={session} />,
  }[shownScreen];

  const runway = kpi?.runwayMonths ?? 999;
  const garage = view.garage;
  const garageBurn = garage ? garage.founders.length * (content.scenario.garage?.personalBurnMonthly ?? 0) : 0;

  // one END TURN in the DOM: topbar normally, pinned in the bottom bar on narrow
  const endTurnBtn = (
    <button className="end-turn" onClick={endTurn} disabled={view.dead}>
      END TURN ▸
      <span className="sub">
        {pending.length} orders{pendingDecisions > 0 ? ` · ${pendingDecisions} decisions` : ""}
        <span className="kbd-hint"> · [enter]</span>
      </span>
    </button>
  );
  const ttyBtn = feedDrawer ? (
    <button className="tty-btn" onClick={() => setFeedOpen((o) => !o)}>
      TTY{unread > 0 && <span className="n">+{unread}</span>}
    </button>
  ) : null;

  return (
    <div className="app">
      <div className="topbar">
        <span className="brand">{isNarrow ? "FS" : "FIRST SILICON"}</span>
        <span className="stat">
          <span className="k">DATE</span>
          <span className="v">{monthLabel(view.monthIndex)}</span>
        </span>
        <span className="stat">
          <span className="k">STAGE</span>
          <span className="v">{view.stage.toUpperCase()}{view.path ? ` · ${view.path.toUpperCase()}` : ""}</span>
        </span>
        {inGarage && garage ? (
          <>
            <span className="stat">
              <span className="k">SAVINGS</span>
              <span className={`v${garage.savings <= 0 ? " bad" : garage.savings < 6 * garageBurn ? " warn" : ""}`}>
                {money(garage.savings)}
              </span>
            </span>
            <span className="stat">
              <span className="k">RAMEN</span>
              <span className={`v${garage.savings <= 0 ? " bad" : ""}`}>
                {garage.savings <= 0 ? "day jobs" : `${Math.floor(garage.savings / Math.max(1, garageBurn))}mo`}
              </span>
            </span>
            <span className="stat">
              <span className="k">FOUNDERS</span>
              <span className="v">{garage.founders.length}</span>
            </span>
            <span className="stat">
              <span className="k">DEMO</span>
              <span className="v">stage {garage.demoStage}</span>
            </span>
          </>
        ) : (
          <>
            <span className="stat">
              <span className="k">CASH</span>
              <span className={`v${view.cash < 500_000 ? " bad" : view.cash < 1_500_000 ? " warn" : ""}`}>{money(view.cash)}</span>
            </span>
            <span className="stat">
              <span className="k">RUNWAY</span>
              <span className={`v${runway < 4 ? " bad" : runway < 9 ? " warn" : ""}`}>{runway >= 999 ? "∞" : `${runway.toFixed(0)}mo`}</span>
            </span>
            <span className="stat">
              <span className="k">TTM REV</span>
              <span className="v">{money(ttm)}</span>
            </span>
            <span className="stat">
              <span className="k">HEADS</span>
              <span className="v">{kpi?.headcount ?? Object.values(content.scenario.founders).reduce((a, b) => a + b, 0)}</span>
            </span>
            <span className="stat">
              <span className="k">FOUNDER</span>
              <span className="v">{pct(view.finance.founderShare, 0)}</span>
            </span>
          </>
        )}
        {view.street && (
          <span className="stat">
            <span className="k">STOCK</span>
            <span className={`v${view.street.sharePrice < 1 ? " bad" : ""}`}>${view.street.sharePrice.toFixed(2)}</span>
          </span>
        )}
        <span className="spacer" />
        {!bottomBar && (
          <span className="topbar-pin">
            {ttyBtn}
            {endTurnBtn}
          </span>
        )}
      </div>

      <nav className="nav">
        {screens.map((s, i) => (
          <button key={s.id} className={shownScreen === s.id ? "active" : ""} onClick={() => setScreen(s.id)}>
            <span className="key">{i + 1 === 10 ? 0 : i + 1} </span>
            {s.label}
            {(s.badge ?? 0) > 0 && <span className="badge">!{s.badge}</span>}
          </button>
        ))}
        <div className="foot">
          <div>
            {content.scenario.id} · seed {snap.seed} · turn {snap.turn}
          </div>
          <button onClick={() => setShowSystem(true)}>≡ SYSTEM</button>
        </div>
      </nav>

      <main className="main">{screenEl}</main>

      <AllocationStrip snap={snap} content={content} />

      {bottomBar && (
        <div className="navbar">
          <div className="tabs">
            {screens.map((s, i) => (
              <button key={s.id} className={shownScreen === s.id ? "active" : ""} onClick={() => setScreen(s.id)}>
                <span className="key">{i + 1 === 10 ? 0 : i + 1} </span>
                {s.label}
                {(s.badge ?? 0) > 0 && <span className="badge">!{s.badge}</span>}
              </button>
            ))}
            <button onClick={() => setShowSystem(true)}>≡</button>
          </div>
          {ttyBtn}
          {endTurnBtn}
        </div>
      )}

      {feedDrawer && feedOpen && <div className="feed-backdrop" onClick={() => setFeedOpen(false)} />}
      {showToast && (
        <div
          className="toast"
          onClick={() => {
            setToastTurn(snap.turn);
            setFeedOpen(true);
          }}
        >
          {rejections.map((r, i) => (
            <div key={i}>✗ order refused: {r.reason}</div>
          ))}
        </div>
      )}

      <aside className={`feed${feedDrawer && feedOpen ? " open" : ""}`} ref={feedRef}>
        {feedDrawer && (
          <button className="feed-close" onClick={() => setFeedOpen(false)}>
            ✕ CLOSE
          </button>
        )}
        <h3>TELETYPE</h3>
        {snap.report && snap.report.rejectedCommands.length > 0 && (
          <div>
            {snap.report.rejectedCommands.map((r, i) => (
              <div key={i} className="rej">✗ order refused: {r.reason}</div>
            ))}
          </div>
        )}
        {[...snap.feed].reverse().map((e, i) => (
          <div key={i} className={`ev${isWarnEvent(e.type) ? " warn" : ""}${isBadEvent(e.type) ? " bad" : ""}${e.type === "history" ? " hist" : ""}`}>
            <span className="m">{monthLabel(e.month)}</span> {e.message}
            {e.type === "history" && typeof e.data.body === "string" && <div className="hist-note">{e.data.body}</div>}
          </div>
        ))}
        <IntroCard content={content} />
      </aside>

      {view.dead && <GameOver snap={snap} content={content} onNewGame={onNewGame} onChooseScenario={onChooseScenario} />}
      {showSystem && (
        <SystemMenu
          content={content}
          session={session}
          onClose={() => setShowSystem(false)}
          onChooseScenario={() => {
            setShowSystem(false);
            onChooseScenario();
          }}
          onLoad={(g) => {
            onLoad(g);
            setShowSystem(false);
          }}
        />
      )}
    </div>
  );
}

/** The turn-zero teletype card — the world as the campaign opens. */
function IntroCard({ content }: { content: ContentPack }) {
  const g = content.scenario.garage;
  if (g) {
    const savings = g.potStart + g.founders.reduce((a, f) => a + f.savingsMonths * g.personalBurnMonthly, 0);
    return (
      <div className="ev">
        <span className="m">{monthLabel(content.scenario.startMonthIndex)}</span> {g.founders.length} of you in a diner booth: {money(savings)} of
        pooled savings, a thesis to pick, and a field that is about to get very crowded. There is no company. Yet.
      </div>
    );
  }
  return (
    <div className="ev">
      <span className="m">{monthLabel(content.scenario.startMonthIndex)}</span> The foundries have proven the model — the first fabless wave is
      already public, and the PC boom is pulling silicon into everything. You have {money(content.scenario.startingCash)} and{" "}
      {Object.values(content.scenario.founders).reduce((a, b) => a + b, 0)} founders.
    </div>
  );
}

function isBadEvent(t: string): boolean {
  return [
    "siliconDead",
    "fieldFailure",
    "bankrupt",
    "roundFailed",
    "boardConcern",
    "boardUltimatum",
    "founderOusted",
    "ipoPulled",
    "delistWarning",
    "delisted",
    "poachRaid",
    "integrationStumble",
    "toolObsoleted",
    "foundrySqueeze",
    "folded",
    "treeObsoleted",
    "devContractCancelled",
  ].includes(t);
}
function isWarnEvent(t: string): boolean {
  return [
    "siliconErrata",
    "respinStarted",
    "prodQualStalled",
    "respinFixStalled",
    "projectCancelled",
    "productEol",
    "askOpened",
    "askExpired",
    "starLeft",
    "acquisitionOffer",
    "termSheetExpired",
    "lockupExpired",
    "pitchResult",
  ].includes(t);
}

const DEATH_COPY = {
  bankrupt: {
    title: "OUT OF CASH",
    body: "The company is dead.",
    hint: "Founder score: $0. Every death teaches something — most 1993 startups never saw first silicon either.",
  },
  ousted: {
    title: "OUSTED BY THE BOARD",
    body: "The directors voted. Security walked you out with a banker's box; the company continues without you.",
    hint: "Founder score: $0. Board seats are the price of capital — confidence is the rent.",
  },
  delisted: {
    title: "DELISTED",
    body: "Six months under a dollar. The pink sheets are where chip companies go to disappear.",
    hint: "Founder score: $0. The public markets giveth in 1999 and taketh away in 2001.",
  },
  acquired: {
    title: "ACQUIRED",
    body: "The papers are signed. Your badge stops working Friday; the products ship under someone else's logo.",
    hint: "Founder score: your take after the waterfall. Most fabless exits looked exactly like this — the trick was selling at the top of the cycle, not the bottom.",
  },
  folded: {
    title: "FOLDED",
    body: "You go back to your old job. The napkins go in a drawer.",
    hint: "Founder score: $0. Of the ~70 companies that chased 3D graphics in the mid-90s, most never incorporated at all.",
  },
} as const;

function GameOver({
  snap,
  content,
  onNewGame,
  onChooseScenario,
}: {
  snap: ReturnType<GameSession["getSnapshot"]>;
  content: ContentPack;
  onNewGame: (scenarioId: string, seed: number) => void;
  onChooseScenario: () => void;
}) {
  const v = snap.view;
  const peak = Math.max(...v.kpiHistory.map((k) => k.revenue), 0);
  const copy = DEATH_COPY[v.deathReason ?? "bankrupt"];
  const shareText =
    v.deathReason === "acquired" && v.finance.exitFounderTake !== null
      ? `I got ACQUIRED in FIRST SILICON — walked away with ${money(v.finance.exitFounderTake)}. Beat my exit: seed ${snap.seed}.`
      : `My chip startup ended: ${copy.title} (${monthLabel(v.monthIndex)}) in FIRST SILICON. Outlast me: seed ${snap.seed}.`;
  return (
    <div className="overlay">
      <div className="box dead">
        <h2>
          {copy.title} — {monthLabel(v.monthIndex)}
        </h2>
        <p>
          {copy.body}{" "}
          {v.deathReason === "acquired" && v.finance.exitFounderTake !== null && (
            <b>Your take after the waterfall: {money(v.finance.exitFounderTake)}. </b>
          )}
          {v.deathReason !== "folded" && (
            <>
              {v.products.length} product{v.products.length === 1 ? "" : "s"} shipped, peak revenue {money(peak)}/mo,{" "}
              {money(v.finance.totalRaised)} raised over {v.finance.roundsCompleted} round{v.finance.roundsCompleted === 1 ? "" : "s"}.
            </>
          )}
        </p>
        <p className="hint">{copy.hint}</p>
        <div className="card .actions" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="act warn" onClick={() => onNewGame(content.scenario.id, Math.floor(Math.random() * 1_000_000))}>
            {v.deathReason === "folded" ? "SCRIBBLE NEW NAPKINS" : "FOUND ANOTHER COMPANY"}
          </button>
          <button className="act" onClick={onChooseScenario}>
            CHOOSE A SCENARIO
          </button>
        </div>
        <div style={{ marginTop: 14 }}>
          <ShareLinks context="game_over" text={shareText} />
        </div>
      </div>
    </div>
  );
}

/** Scenario select (P1, spec §11): cards replace the seed-only new-game flow. */
function ScenarioSelect({ onStart }: { onStart: (scenarioId: string, seed: number) => void }) {
  const [seedText, setSeedText] = useState(String(Math.floor(Math.random() * 1_000_000)));
  const seed = Number(seedText) || 1;
  return (
    <div className="overlay" style={{ background: "var(--bg)" }}>
      <div className="box wide">
        <h2>FIRST SILICON</h2>
        <p className="hint">Pick a campaign. Each scenario is a market, a start date, and a storyline — the engine underneath is the same silicon.</p>
        {SCENARIOS.map((sc) => (
          <ScenarioCard key={sc.id} sc={sc} onStart={() => onStart(sc.id, seed)} />
        ))}
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <label>
            seed <input type="text" value={seedText} onChange={(e) => setSeedText(e.target.value.replace(/[^0-9]/g, ""))} style={{ width: 90 }} />
          </label>
          <span className="hint">same seed, same world — share it with a rival founder</span>
        </div>
        <div style={{ marginTop: 14 }}>
          <ShareLinks context="title" />
        </div>
      </div>
    </div>
  );
}

function ScenarioCard({ sc, onStart }: { sc: ScenarioMeta; onStart: () => void }) {
  return (
    <div className="panel scenario-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "var(--amber)", letterSpacing: 1, fontWeight: 700 }}>{sc.title}</div>
          <div className="hint">
            {sc.dateLabel} · {sc.marketLabel}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {sc.tags.map((t) => (
            <span key={t} className="badge cyan">
              {t}
            </span>
          ))}
        </div>
      </div>
      <p style={{ margin: "8px 0", fontSize: 12 }}>{sc.blurb}</p>
      <button className="act warn" onClick={onStart}>
        BEGIN — {sc.title.split("—")[0]!.trim()} ▸
      </button>
    </div>
  );
}

/** System menu: save slots, export/import, new game (design §16 M2 "save/load"). */
function SystemMenu({
  content,
  session,
  onClose,
  onChooseScenario,
  onLoad,
}: {
  content: ContentPack;
  session: GameSession;
  onClose: () => void;
  onChooseScenario: () => void;
  onLoad: (g: Game) => void;
}) {
  const [importError, setImportError] = useState<string | null>(null);
  const [, bump] = useState(0); // slot writes are outside React state — re-read on demand
  const slots = Array.from({ length: GameSession.SLOT_COUNT }, (_, i) => GameSession.slotInfo(content, i + 1));

  const exportFile = () => {
    const snap = session.getSnapshot();
    const blob = new Blob([session.exportSave()], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `first-silicon-${content.scenario.id}-seed${snap.seed}-turn${snap.turn}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const importFile = (file: File) => {
    void file.text().then((text) => {
      const scenarioId = GameSession.peekScenarioId(text);
      if (!scenarioId) {
        setImportError("not a FIRST SILICON save file");
        return;
      }
      let pack: ContentPack;
      try {
        pack = loadBrowserContent(scenarioId);
      } catch {
        setImportError(`save is for unknown scenario ${scenarioId}`);
        return;
      }
      const result = GameSession.import(pack, text);
      if (typeof result === "string") setImportError(result);
      else {
        track("save_import", { scenarioId });
        onLoad({ content: pack, session: result });
      }
    });
  };

  const loadSlot = (slot: number, scenarioId: string) => {
    try {
      const pack = loadBrowserContent(scenarioId);
      const s = GameSession.loadSlot(pack, slot);
      if (s) onLoad({ content: pack, session: s });
    } catch {
      // unknown scenario in the slot: leave it alone
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="box" onClick={(e) => e.stopPropagation()}>
        <h2>SYSTEM</h2>
        <div className="panel">
          <div className="title">SAVE SLOTS</div>
          {slots.map((info, i) => (
            <div key={i} className="p" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
              <span style={{ flex: "1 1 220px" }}>
                SLOT {i + 1}:{" "}
                {info
                  ? `${info.scenarioId} · seed ${info.seed} · ${monthLabel(info.turn)}${info.compatible ? "" : " (old content — unloadable)"}`
                  : "empty"}
              </span>
              <button className="act" onClick={() => { session.saveToSlot(i + 1); bump((x) => x + 1); }}>
                SAVE
              </button>
              {info?.compatible && (
                <button className="act warn" onClick={() => loadSlot(i + 1, info.scenarioId)}>
                  LOAD
                </button>
              )}
              {info && (
                <button className="act bad" onClick={() => { GameSession.clearSlot(i + 1); bump((x) => x + 1); }}>
                  ✗
                </button>
              )}
            </div>
          ))}
          <span className="hint">the autosave (every order and turn) is separate — slots are deliberate checkpoints</span>
        </div>
        <div className="panel">
          <div className="title">SAVE FILES</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button className="act" onClick={exportFile}>
              EXPORT SAVE (.json)
            </button>
            <label className="act" style={{ cursor: "pointer" }}>
              IMPORT SAVE
              <input
                type="file"
                accept="application/json"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) importFile(f);
                }}
              />
            </label>
          </div>
          {importError && <div className="bad-text">✗ {importError}</div>}
          <span className="hint">saves are event-sourced (seed + command log) — small, shareable, and perfect for bug reports</span>
        </div>
        <div className="panel">
          <div className="title">NEW GAME</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button className="act warn" onClick={onChooseScenario}>
              CHOOSE A SCENARIO ▸
            </button>
            <span className="hint">the current autosave survives until a new campaign begins (slots always survive)</span>
          </div>
        </div>
        {window.matchMedia("(pointer: coarse)").matches && !isStandalone() && (
          <div className="panel">
            <div className="title">FULL SCREEN</div>
            <span className="hint">
              browsers keep their bars on screen — install instead: Share → Add to Home Screen (iPhone) or menu → Add to Home
              screen (Android). The installed game launches full screen.
            </span>
          </div>
        )}
        <div className="panel">
          <div className="title">SPREAD THE WORD</div>
          <ShareLinks context="system" />
          <span className="hint">word of mouth is the whole marketing department</span>
        </div>
        <button className="act" onClick={onClose}>
          CLOSE [esc]
        </button>
      </div>
    </div>
  );
}
